'use strict';

define('vb/action/builtin/callVariableMethodAction',[
  'vb/action/action',
  'vb/private/utils',
  'vb/private/stateManagement/stateUtils',
  'vb/private/log',
  'vb/private/constants',
  'vb/action/builtin/assignVariablesAction',
],
(Action, Utils, StateUtils, Log, Constants, AssignVariablesAction) => {
  /** @typedef {import('vb/action/action').Outcome} Outcome */

  /**
   * Invoke a method on a variable of instanceFactory type.
   *
   * The parameters to this action are as follows:
   *
   * variable: a variable, e.g., $page.variables.foo
   * method: the method to invoke
   * params: the parameters to the method
   *
   * @extends AssignVariablesAction
   * @category Action
   */
  class CallVariableMethodAction extends AssignVariablesAction {
    /**
     * Constructs a new instance of the CallVariableMethodAction class.
     *
     * @param {string} id - The unique identifier for this action.
     * @param {string} label - The human-readable label for this action.
     */
    constructor(id, label) {
      super(id, label);
      this.log = Log.getLogger('/vb/action/action/CallVariableFunctionAction');
    }

    /**
     * Invokes a method on a variable of instanceFactory type.
     *
     * @async
     * @param {object} parameters - An object containing the following properties:
     *   @property {string} variable - The name of the variable to invoke the method on.
     *   @property {string} method - The name of the method to invoke.
     *   @property {array} [params] - An optional array of parameters to pass to the method.
     * @returns {Outcome}
     *   A promise that resolves with an Outcome object representing the result of the method invocation.
     *   The promise may also reject with an error message if any errors occur during execution.
     * @throws {Error} If the variable is not an instance factory variable, or if the method does not exist on the
     * variable, or if the inputs are not in the correct format.
     */
    perform(parameters) {
      const { variable, method, params } = parameters;

      // locate variable and instance property on it
      const targetInfo = this.analyzeTargetExpr(variable,
        { availableContexts: this.availableContexts });
      const instanceFactoryVar = targetInfo.typeClassification === Constants.VariableClassification.INSTANCE_FACTORY;

      if (!instanceFactoryVar) {
        const msg = `Action ${this.logLabel} cannot be used with non instance factory variable ${variable}`;
        this.log.error(msg);
        return Promise.reject(msg);
      }
      const varValue = targetInfo.rootValue;
      if (!varValue) {
        const msg = `Action ${this.logLabel} could not read the value of variable ${variable}`;
        this.log.error(msg);
        return Promise.reject(msg);
      }
      const instance = varValue.instance;
      if (!instance || !method || typeof instance[method] !== 'function') {
        const msg = `Action ${this.logLabel} cannot call method ${method} on variable ${variable}`;
        this.log.error(msg);
        return Promise.reject(msg);
      }

      if (params && !Array.isArray(params)) {
        const msg = `Action ${this.logLabel}: Inputs to CallVariableFunctionAction`
          + ' should be an array, even if it is a single item.';
        this.log.error(msg);
        return Promise.reject(msg);
      }

      const clonedParams = [];
      if (params) {
        Utils.cloneObject(params, clonedParams);
      }

      const func = instance[method];
      let ret;
      try {
        ret = func.apply(instance, clonedParams);
      } catch (e) {
        const msg = `Action ${this.logLabel}: error calling function: ${method}(${params})`;
        this.log.error(msg, e);
        return Promise.resolve(Action.createFailureOutcome(msg, e));
      }

      return Promise.resolve(ret)
        .then((result) => Action.createSuccessOutcome(result))
        .catch((e) => {
          const msg = `Action ${this.logLabel}: error calling function: ${method}(${params || ''})`;
          return Action.createFailureOutcome(msg, e);
        });
    }

    /**
     * Sets the available contexts for expression evaluations.
     *
     * @param {object} availableContexts - An object containing the available contexts, such as `$application`,
     * `$page`, etc.
     * @public
     */
    setAvailableContext(availableContexts) {
      this.availableContexts = availableContexts;
    }
  }

  return CallVariableMethodAction;
});

